import { useState, useEffect } from "react";

// Hooks
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "app/hooks";

// Store
import { showLoader } from "store/slice/UI";
import { clearChart } from "store/slice/charts";
import { getShortList } from "store/slice/refinement";
import { getBoardStructureData } from "store/slice/Team/boardStructure";
import { getSearchAiById, updateSearchAiChild } from "store/slice/Project/searchAis/searchAisSlice";
import {
    getTeamsCustomBoardStructure,
    setCustomBoardStructure,
} from "store/slice/Team/boardStructure/boardStructureSlice";
import { getNotInterstedLongList, useLongListSlice } from "store/slice/refinement/longList/longListSlice";

// Enums
import { urlPaths } from "enums/urlPaths";

// Services
import { saveProjectByPathService, updateProjectByPathService } from "services";

// Types
import { LocationFrom } from "types";
import { NavButtonProps } from "components/HeaderNavigation/NavButton";

// Utils
import { buildParamsString } from "utils";
import { values, merge, keyBy, isEmpty } from "lodash";
import {
    getInsightPeersPath,
    getInsightRelatedSltPath,
    getInsightRelatedVcpData,
    getSearchAiLeadersLongList,
    getSearchAiLeadersShortList,
} from "pages/ProjectDashboard/adapters/formattedData";
import { addPersonSet, updatePersonSet, getPersonSetByPath } from "store/slice/Project/peopleSet/peopleSetSlice";
import { shallowEqual } from "react-redux";
import { getInsightById, updateInsightChild } from "store/slice/Project/insights/insightsSlice";
import { ProjectPersonSet } from "services/projects/project.types";

type useSetTeamNavActionsProps = (currentSearchAiId?: number) => {
    backButton: NavButtonProps;
    nextButton: NavButtonProps;
};

const useSetTeamNavActions: useSetTeamNavActionsProps = (currentSearchAiId) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const location = useLocation() as unknown as LocationFrom;

    const [searchParams] = useSearchParams();

    const { companyId } = useParams();
    const projectId = searchParams.get("projectId");
    const insightId = searchParams.get("insightId");
    const briefId = searchParams.get("briefId");

    const { data: insightData } = useAppSelector(getInsightById(Number(insightId)), shallowEqual);
    const { data: searchAiData } = useAppSelector(getSearchAiById(Number(currentSearchAiId)), shallowEqual);

    const boardStructureList = useAppSelector(getBoardStructureData);
    const customBoardStructureList = useAppSelector(getTeamsCustomBoardStructure);

    const { longList } = useLongListSlice();
    const shortList = useAppSelector(getShortList);
    const notInterestedList = useAppSelector(getNotInterstedLongList);

    const [buttonLabel, setbuttonLabel] = useState("button.next");
    const [shouldDisableButton, setShouldDisableButton] = useState(false);

    const relatedPeersPath = getInsightPeersPath(insightData)?.ref?.path;
    const relatedSltPath = getInsightRelatedSltPath(insightData)?.ref?.path;
    const relatedVcpData = getInsightRelatedVcpData(insightData)?.data;
    const canGotoInsights = Boolean(relatedPeersPath && relatedSltPath && relatedVcpData?.id);

    const longListSetPath = getSearchAiLeadersLongList(searchAiData)?.ref?.path;
    const shortListSetPath = getSearchAiLeadersShortList(searchAiData)?.ref?.path;

    const sltData = useAppSelector(getPersonSetByPath(relatedSltPath), shallowEqual);
    const longlistData = useAppSelector(getPersonSetByPath(longListSetPath), shallowEqual);
    const shortlistData = useAppSelector(getPersonSetByPath(shortListSetPath), shallowEqual);

    // if isEditing
    useEffect(() => {
        canGotoInsights && setbuttonLabel("button.finish.refinement");
    }, [canGotoInsights]);

    useEffect(() => {
        if (!boardStructureList?.length || boardStructureList.some((member) => !member.roleKey)) {
            setShouldDisableButton(true);
        } else {
            setShouldDisableButton(false);
        }
    }, [boardStructureList]);

    const handleBack = () => {
        const queryParams = buildParamsString({
            projectId,
            insightId,
            briefId,
            searchAiId: currentSearchAiId?.toString(),
        });

        const urlParams = `${companyId}?${queryParams}`;

        // Coming from no SLT Insight or Talent People page
        if (
            location.state?.from?.includes(urlPaths.TalentPeople) ||
            location.state?.from?.includes(urlPaths.Projection) ||
            location.state?.from?.includes(urlPaths.TalentRefinement) ||
            location.state?.from?.includes(urlPaths.ProjectDashboard)
        ) {
            navigate(location.state.from, {
                state: { from: `${urlPaths.Team}/${urlParams}` },
            });
            return;
        }

        navigate(`${urlPaths.Domain}/${urlParams}`, {
            state: { from: `${urlPaths.Team}/${urlParams}` },
        });
    };

    const handleNext = async () => {
        setShouldDisableButton(true);
        dispatch(showLoader(true));

        // Update shortlist and longlist
        if (currentSearchAiId) {
            try {
                const longListRef = getSearchAiLeadersLongList(searchAiData)?.ref?.path;
                const shortListRef = getSearchAiLeadersShortList(searchAiData)?.ref?.path;

                if (longListRef) {
                    const payload = {
                        data: {
                            person_ids: [...longList, ...notInterestedList].map((l) => Number(l.person_id)),
                        },
                        meta_ui: {
                            person_ids: [...longList, ...notInterestedList].map((l) => ({
                                id: Number(l.person_id),
                                ...(l.roleKey ? { role_id: Number(l.roleKey) } : {}),
                                ...(l.in ? { in: l.in } : {}),
                            })),
                        },
                    };

                    await updateProjectByPathService({
                        id: Number(projectId),
                        project_path: longListRef,
                        data: payload,
                    });

                    if (longlistData) {
                        const updatedData = {
                            ...longlistData,
                            meta: { ...longlistData.meta, count: payload.data.person_ids.length },
                            data: { ...longlistData.data, person_ids: payload.data.person_ids },
                            meta_ui: payload.meta_ui,
                        };
                        dispatch(updatePersonSet(updatedData));
                    }
                }

                if (shortListRef) {
                    // Update Shortlist
                    const payload = {
                        data: {
                            person_ids: shortList.map((s) => Number(s.person_id)),
                        },
                        meta_ui: {
                            person_ids: shortList.map((s) => ({
                                id: Number(s.person_id),
                                role_id: Number(s.roleKey),
                                ...(s.statusUI ? { status_ui: s.statusUI } : {}),
                            })),
                        },
                    };

                    await updateProjectByPathService({
                        id: Number(projectId),
                        project_path: shortListRef,
                        data: payload,
                    });

                    if (shortlistData) {
                        const updatedData = {
                            ...shortlistData,
                            meta: { ...shortlistData.meta, count: payload.data.person_ids.length },
                            data: { ...shortlistData.data, person_ids: payload.data.person_ids },
                            meta_ui: payload.meta_ui,
                        };
                        dispatch(updatePersonSet(updatedData));
                    }
                } else {
                    // Create Shortlist and update ref in SearchAI/People
                    const { data } = await saveProjectByPathService({
                        id: Number(projectId),
                        project_path: `/project_${projectId}/person_set_list`,
                        data: {
                            data: {
                                name: `Short list ` + `${new Date().getTime()}`.slice(-5),
                                type: "SHORT_LIST",
                                person_ids: shortList.map((s) => Number(s.person_id)),
                            },
                            meta_ui: {
                                person_ids: shortList.map((s) => ({
                                    id: Number(s.person_id),
                                    role_id: Number(s.roleKey),
                                    ...(s.statusUI ? { status_ui: s.statusUI } : {}),
                                })),
                            },
                        },
                        echo: true,
                    });

                    const peopleSet = data.children.at(-1);
                    const peopleSetId = peopleSet.data.id;

                    if (!isEmpty(peopleSet)) {
                        dispatch(addPersonSet([peopleSet]));
                    }

                    // Update ref on corresponding SearchAI
                    const { data: searchAiData } = await updateProjectByPathService({
                        id: Number(projectId),
                        project_path: `/project_${projectId}/search_ai_list/${currentSearchAiId}/short_list`,
                        data: {
                            data: {},
                            ref: {
                                path: `/project_${projectId}/person_set_list/person_set_${peopleSetId}`,
                            },
                        },
                    });

                    if (!isEmpty(searchAiData)) {
                        dispatch(
                            updateSearchAiChild({
                                data: searchAiData,
                                id: Number(currentSearchAiId),
                                type: "PROJECT_PERSON_SET_REF_B",
                            })
                        );
                    }
                }
            } catch (error) {
                console.error(error);
                dispatch(showLoader(false));
                setShouldDisableButton(false);
                return;
            }
        }

        if (relatedSltPath) {
            // Updating
            const relatedPersonSetRef = getInsightRelatedSltPath(insightData)?.ref?.path;

            if (!relatedPersonSetRef) return;

            const membersToSave = currentSearchAiId
                ? boardStructureList
                : values(merge(keyBy(boardStructureList, "person_id"), keyBy(customBoardStructureList, "person_id")));

            try {
                const payload = {
                    data: {
                        person_ids: membersToSave.map((m) => Number(m.person_id)),
                    },
                    meta_ui: {
                        person_ids: membersToSave.map((m) => ({
                            id: Number(m.person_id),
                            role_id: Number(m.roleKey),
                            ...(m.in ? { in: m.in } : {}),
                            ...(m.meta ? { meta: m.meta } : {}),
                        })),
                    },
                };

                await updateProjectByPathService({
                    id: Number(projectId),
                    project_path: relatedPersonSetRef,
                    data: payload,
                });

                if (sltData) {
                    const updatedData = {
                        ...sltData,
                        meta: { ...sltData.meta, count: payload.data.person_ids.length },
                        data: { ...sltData.data, person_ids: payload.data.person_ids },
                        meta_ui: payload.meta_ui,
                    };
                    dispatch(updatePersonSet(updatedData));
                }
            } catch (error) {
                console.error(error);
                dispatch(showLoader(false));
                setShouldDisableButton(false);
                return;
            }

            dispatch(setCustomBoardStructure([]));
            dispatch(showLoader(false));

            const queryParams = buildParamsString({
                projectId,
                insightId,
                briefId,
                searchAiId: currentSearchAiId?.toString(),
            });
            const urlParams = `${companyId}?${queryParams}`;

            if (
                location.state?.from?.includes(urlPaths.TalentPeople) ||
                location.state?.from?.includes(urlPaths.Projection) ||
                location.state?.from?.includes(urlPaths.TalentRefinement)
            ) {
                dispatch(clearChart());
                navigate(location.state.from, {
                    state: { from: `${urlPaths.Team}/${urlParams}` },
                });
                return;
            }

            if (canGotoInsights) {
                dispatch(clearChart());
                navigate(`${urlPaths.Projection}/${urlParams}`);
            } else {
                navigate(`${urlPaths.Vcp}/${urlParams}`);
            }
        } else {
            // Creating
            let peopleSet = {} as ProjectPersonSet;

            try {
                const { data } = await saveProjectByPathService({
                    id: Number(projectId),
                    project_path: `/project_${projectId}/person_set_list`,
                    data: {
                        data: {
                            name: `SLT ` + `${new Date().getTime()}`.slice(-5),
                            type: "SLT",
                            person_ids: boardStructureList.map((m) => Number(m.person_id)),
                        },
                        meta_ui: {
                            person_ids: boardStructureList.map((m) => ({
                                id: Number(m.person_id),
                                role_id: Number(m.roleKey),
                                ...(m.in ? { in: m.in } : {}),
                            })),
                        },
                    },
                    echo: true,
                });

                peopleSet = data.children.at(-1);

                if (!isEmpty(peopleSet)) {
                    dispatch(addPersonSet([peopleSet]));
                }
            } catch (error) {
                console.error(error);
                dispatch(showLoader(false));
                setShouldDisableButton(false);
                return;
            }

            // Update ref on corresponding Insight
            if (insightId) {
                try {
                    const payload = {
                        data: {},
                        ref: {
                            path: `/project_${projectId}/person_set_list/person_set_${peopleSet.data.id}`,
                        },
                    };

                    await updateProjectByPathService({
                        id: Number(projectId),
                        project_path: `/project_${projectId}/insight_list/insight_${insightId}/slt`,
                        data: payload,
                    });

                    const insightSltSetRef = getInsightRelatedSltPath(insightData);

                    if (insightSltSetRef && insightId) {
                        const updatedData = {
                            ...insightSltSetRef,
                            ref: payload.ref,
                        };

                        dispatch(
                            updateInsightChild({
                                data: updatedData,
                                id: Number(insightId),
                                type: "PROJECT_PERSON_SET_REF",
                            })
                        );
                    }
                } catch (error) {
                    console.error(error);
                    dispatch(showLoader(false));
                    setShouldDisableButton(false);
                    return;
                }
            }

            navigate(`${urlPaths.Vcp}/${companyId}?projectId=${projectId}&insightId=${insightId}`);
        }
    };

    return {
        backButton: {
            onClick: handleBack,
        },
        nextButton: {
            translationId: buttonLabel,
            disabled: shouldDisableButton,
            onClick: handleNext,
        },
    };
};

export default useSetTeamNavActions;
